import { useEffect, useState } from "react";

import {
  clearImpersonationSession,
  ImpersonationSession,
  readImpersonationSession,
} from "@/utils/auth/impersonation";

const formatRemaining = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, "0");

  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
};

const getRemainingMs = (session: ImpersonationSession | null): number =>
  session ? new Date(session.expiresAt).getTime() - Date.now() : 0;

export const useImpersonationSession = () => {
  const [session, setSession] = useState(() => readImpersonationSession());
  const [remainingMs, setRemainingMs] = useState(() => getRemainingMs(session));

  useEffect(() => {
    if (!session) return;

    const tick = () => {
      const ms = getRemainingMs(session);
      if (ms <= 0) {
        clearImpersonationSession();
        setSession(null);
        return;
      }
      setRemainingMs(ms);
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [session]);

  const endSession = () => {
    clearImpersonationSession();
    window.location.reload();
  };

  return {
    session,
    isImpersonating: session !== null,
    remainingMs,
    formattedRemaining: formatRemaining(remainingMs),
    endSession,
  };
};
